import Vue from "vue";
import {Plan, User} from "@/model/User";
import {TranslateResult} from "vue-i18n";
import {getPlanLimits, PlanLimits} from "@/config/UnlimitedPlan";
import {articleAccessFromStore, usageChipModel, UsageChipModel} from "@/service/articleLimitGate";

export default Vue.extend({
    name: 'UserPlanMixin',
    computed: {
        // ===== USER & PLAN BASICS =====
        user: function (): User {
            return this.$store.getters.user;
        },
        plan: function (): Plan | null {
            return this.$store.getters.plan;
        },
        hasPlan: function (): boolean {
            return this.$store.getters.hasPlan;
        },
        planLoaded: function (): boolean {
            return this.$store.getters.planLoaded;
        },
        usageCounterLoaded: function (): boolean {
            return this.$store.getters.usageCounterLoaded;
        },
        usageChip: function (): UsageChipModel {
            return usageChipModel({
                planLoaded: this.planLoaded,
                usageLoaded: this.usageCounterLoaded,
                plan: this.plan,
                freeAnalysesLeft: this.freeAnalysesLeft,
                maxFreeAnalyses: this.maxFreeAnalyses,
                articlesCreated: this.articlesCreatedThisPeriod,
            });
        },
        planName: function (): TranslateResult | string {
            return this.plan?.name || this.$t('subscriptions.noPlan');
        },
        planPeriodEnd: function (): Date | undefined {
            return this.plan?.period_end;
        },
        
        // ===== PLAN TYPE DETECTION =====
        hasLimitedPlan: function (): boolean {
            return this.hasPlan && this.plan?.monthly_credit !== Infinity;
        },
        hasUnlimitedPlan: function (): boolean {
            return this.hasPlan && this.plan?.monthly_credit === Infinity;
        },
        hasUsageLimitedPlan: function (): boolean {
            return this.hasUnlimitedPlan && this.plan?.metadata?.usageLimit === 'true';
        },
        
        // ===== CREDIT TRACKING =====
        articlesCreatedThisPeriod: function (): number {
            return this.$store.getters.user?.articlesCreatedThisPeriod || 0;
        },
        loadingArticlesCreatedThisPeriod: function (): boolean {
            if (!this.user) {
                return true;
            }
            return this.$store.getters.user.articlesCreatedThisPeriod === undefined;
        },
        maxInspirationsThisPeriod: function (): number {
            return this.plan?.monthly_credit || 0;
        },
        articlesLeftThisPeriod: function (): number {
            if (!this.plan) return 0;
            if (this.hasUnlimitedPlan) return Infinity;
            return Math.max(this.maxInspirationsThisPeriod - this.articlesCreatedThisPeriod, 0);
        },
        extraInspirations: function (): number {
            return this.$store.getters.user?.extraInspirations || 0;
        },
        freeAnalysesLeft: function (): number {
            const left = this.$store.getters.user?.freeAnalysesLeft;
            if (typeof left !== 'number' || Number.isNaN(left) || left < 0) {
                return this.maxFreeAnalyses;
            }
            return left;
        },
        maxFreeAnalyses: function (): number {
            return this.$store.getters.maxFreeAnalyses;
        },
        analyzeArticlesThisMonth: function (): number {
            return this.$store.getters.analyzeArticlesThisMonth || 0;
        },
        freeAnalysesUsed: function (): number {
            return Math.max(0, this.maxFreeAnalyses - this.freeAnalysesLeft);
        },
        hasFreeAnalyses: function (): boolean {
            return this.freeAnalysesLeft > 0;
        },
        
        // ===== CREDIT AVAILABILITY CHECKS =====
        hasCreditForAnalyze: function (): boolean {
            return articleAccessFromStore(this.$store, 'analyze').action !== 'paywall';
        },
        hasCreditForGenerate: function (): boolean {
            return articleAccessFromStore(this.$store, 'generate').action !== 'paywall';
        },
        hasAnyCredit: function (): boolean {
            return this.hasCreditForAnalyze || this.hasCreditForGenerate;
        },
        
        // ===== USAGE LIMITS =====
        currentPlanLimits: function (): PlanLimits {
            const currentPlan = this.plan as Plan | null;
            return getPlanLimits(currentPlan?.name);
        },
        hasReachedWarningLimit: function (): boolean {
            if (!this.hasUsageLimitedPlan) return false;
            return this.articlesCreatedThisPeriod >= this.currentPlanLimits.warning;
        },
        hasReachedStopLimit: function (): boolean {
            if (!this.hasUsageLimitedPlan) return false;
            return this.articlesCreatedThisPeriod >= this.currentPlanLimits.stop;
        },
        
        // ===== UI HELPERS =====
        usageTooltip: function (): TranslateResult {
            if (!this.planLoaded || !this.usageCounterLoaded) {
                return this.$t('subscriptions.usageLoading');
            }
            if (this.hasUnlimitedPlan) {
                return this.$t('subscriptions.unlimitedUsageTooltip', [this.articlesCreatedThisPeriod])
            } else {
                return this.$t('subscriptions.usageTooltip', [
                    this.articlesCreatedThisPeriod,
                    this.maxInspirationsThisPeriod
                ]);
            }
        },
        planRenewTooltip: function (): TranslateResult {
            if (this.plan?.cancel_at_period_end) {
                return this.$t('subscriptions.planWillBeCancelled');
            } else {
                return this.$t('subscriptions.planRenewTooltip');
            }
        },
        usageColor: function (): string {
            if (!this.plan) {
                if (this.extraInspirations > 0) return 'warning';
                if (this.freeAnalysesLeft <= 0) return 'error';
                if (this.freeAnalysesLeft <= 1) return 'warning';
                return 'accent';
            }
            
            if (this.hasUnlimitedPlan) {
                // Unlimited plan - always green (or warning if has extra inspirations)
                return this.extraInspirations > 0 ? 'warning' : 'accent';
            }
            
            // Limited plan - color based on usage percentage
            const coeff = this.articlesCreatedThisPeriod / this.maxInspirationsThisPeriod;
            if (coeff <= 0.75) return 'accent';
            if (coeff < 1) return 'warning';
            return this.extraInspirations > 0 ? 'warning' : 'error';
        }
    }
});
